import { Autorenew } from "@mui/icons-material";
import { alpha, Box, Card, CardContent, CardHeader, Skeleton, Stack, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { DATE_TIME_FORMAT } from "../../app/constants/common";
import { serverToUi } from "../../app/utils/utilityFunctions";
import { EmptyDataVisualComp, UiIconButton } from "../../components/ui/UiUtils";
import { useGetCustomerTrendsMutation } from "../../hooks/ApiHooks";
import { buildStackedSeries } from "./config/customerTrendsConfig";

const getChartOptions = (theme, categories = []) => {
  return {
    chart: {
      background: "transparent",
      type: "bar",
      stacked: true,
      toolbar: { show: false },
    },
    fill: {
      type: "solid",
      opacity: 1,
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: alpha(theme.palette.divider, 0.1),
      xaxis: { lines: { show: true } },
    },
    legend: {
      show: true,
      position: "bottom",
      labels: { colors: theme.palette.text.secondary },
      markers: { shape: "circle" },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "40px",
        dataLabels: { position: "top" },
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: [theme.palette.background.paper],
    },
    theme: { mode: theme.palette.mode },
    xaxis: {
      categories,
      axisBorder: {
        color: theme.palette.divider,
        show: true,
      },
      axisTicks: {
        color: theme.palette.divider,
        show: true,
      },
      labels: {
        offsetY: 5,
        style: { colors: theme.palette.text.secondary },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => `${value}`,
        offsetX: -10,
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
    title: {
      text: "Customers x Task Type",
      floating: true,
      offsetY: 0,
      align: "right",
      style: {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontFamily: "'Poppins', sans-serif",
        fontSize: "18px",
        marginBottom: "10px",
      },
    },
  };
};

const ChartSkeleton = () => {
  const skeletonBars = Array(13).fill(null);

  const getRandomHeight = () => {
    return Math.floor(Math.random() * 60 + 30);
  };

  return (
    <Box
      sx={{
        height: 350,
        position: "relative",
      }}
      display="flex"
      alignItems="stretch"
      gap={3}
    >
      {/* Y-axis labels */}
      <Stack justifyContent="space-evenly">
        {skeletonBars.map((_, index) => (
          <Skeleton
            animation="wave"
            key={index}
            variant="rectangular"
            width={20}
            height={10}
            sx={{
              borderRadius: 1,
            }}
          />
        ))}
      </Stack>

      {/* Main chart area */}
      <Stack flex="1" justifyContent="space-between" alignItems="end">
        {/* Title and legend */}
        <Skeleton
          variant="text"
          animation="wave"
          width="20%"
          height={32}
          sx={{
            mb: 2,
          }}
        />

        {/* Chart bars container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            height: "100%",
            width: "100%",
            gap: 2,
            px: 2,
            position: "relative",
          }}
        >
          {/* Bars */}
          {skeletonBars.map((_, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                height: `${getRandomHeight()}%`,
              }}
            >
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  width: 40,
                  height: `100%`,
                  borderRadius: 1,
                  marginX: "auto", // Centers the bar in its container
                }}
              />
              {/* X-axis labels */}
              <Skeleton
                variant="text"
                width={40}
                height={20}
                sx={{
                  marginX: "auto",
                  mt: 2,
                }}
              />
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>
  );
};

export default function CustomerMonthlyCountChart({ from, to }) {
  const theme = useTheme();
  const { triggerMutation: getCustomerTrends, isLoading, isSuccess, data } = useGetCustomerTrendsMutation();

  useEffect(() => {
    getCustomerTrends(from, to);
  }, [getCustomerTrends, from, to]);

  const refetchData = useCallback(() => getCustomerTrends(from, to), [getCustomerTrends, from, to]);

  const chartData = useMemo(() => {
    const categories = data?.data?.categories ?? [];
    const series = buildStackedSeries(data?.data?.series ?? [], categories, theme.palette.mode);
    return { categories, series };
  }, [data, theme.palette.mode]);

  const periodDisplayLabel = useMemo(
    () => `${serverToUi(from, DATE_TIME_FORMAT.UI_DATE_FORMAT) || "No Min Date"} - ${serverToUi(to, DATE_TIME_FORMAT.UI_DATE_FORMAT) || "Present Date"}`,
    [from, to]
  );

  return (
    <Card
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 4,
        border: "1px solid",
        borderColor: "primary.sectionBorder600",
      }}
    >
      <CardHeader
        title={`Customer Monthly Trends - ${periodDisplayLabel}`}
        action={
          <UiIconButton title="Refetch Data" onClickIcon={refetchData} disabled={isLoading}>
            <Autorenew />
          </UiIconButton>
        }
      />
      <CardContent>
        {isLoading ? (
          <ChartSkeleton />
        ) : !isSuccess ? (
          <EmptyDataVisualComp title="Error Loading Data" subTitle="There is no data to display for the selected period." />
        ) : (
          <Chart height={350} options={getChartOptions(theme, chartData.categories)} series={chartData.series} type="bar" width="100%" />
        )}
      </CardContent>
    </Card>
  );
}
